import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import classNames from 'classnames';

import { Box, Button, Popup } from '@missionlabs/smartagent-app-components';

import useHasFeature, { AppFeatures } from 'hooks/useHasFeature';
import useHasMic from 'hooks/useHasMic';
import useCall from 'hooks/redux/useCall';
import useMonitoring from 'hooks/redux/useMonitoring';

import RootState from 'store/state';
import { clearContact } from 'store/contact/contact.actions';
import { completeCallACW, setSelectedCall } from 'store/call/call.actions';
import { setState } from 'store/user/user.actions';

import AfterCallWork from './AfterCallWork';
import CallHistory from './CallHistory';
import ContactLog from './ContactLog';
import CustomerInputs from './CustomerInputs';
import Dialler from './Dialler';
import Monitoring from './Monitoring';
import DPA from './DPA';
import ReportIssue from './ReportIssue';
import CallRecordingControl from './CallRecordingControl';

import { ContactPanel } from '../ContactPanel';

import './home.scss';

export const HomeContext = React.createContext<IHomeContext>({});
interface IHomeContext {
    diallerOpened?: boolean;
    setDiallerOpened?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Props {}

const Home: React.FC<Props> = () => {
    const dispatch = useDispatch();

    const hasFeature = useHasFeature();
    const { hasMic, clearFails } = useHasMic();
    const { isMonitoringOrBarging, isEnhancedMonitoringEnabled } = useMonitoring();
    const { connections = [] } = useCall() ?? {};

    const [diallerOpened, setDiallerOpened] = useState(false);

    const nextAgentStatus = useSelector((state: RootState) => state.user.afterCallStatus);
    const softphoneEnabled = useSelector((state: RootState) => state.user.softphoneEnabled);
    const currentStatus = useSelector((state: RootState) => state.user.status.name) as connect.AgentAvailStates;

    const contact = useSelector((state: RootState) => state.contact);
    const channel = contact?.channel;

    const { AFTER_CALL_WORK, BUSY } = connect.AgentAvailStates;

    const contactLogStatuses = [AFTER_CALL_WORK, BUSY];
    const inContactLogStatus = contactLogStatuses.includes(currentStatus);

    const hasContact = !!contact;
    const inCall = connections.length > 0;

    // When enhanced monitoring is disabled in Connect, CTRs are not generated for monitored voice contacts
    // So we do not render out the contact log for these contacts
    const contactLogAvailable = inContactLogStatus || (hasContact && inCall);
    const showContactLog = isMonitoringOrBarging
        ? contactLogAvailable && isEnhancedMonitoringEnabled
        : contactLogAvailable;
    const showAfterCallWork = currentStatus === AFTER_CALL_WORK;

    const onEndACW = () => {
        // Temporary batch wrap until we've fully migrated to React 18
        batch(() => {
            nextAgentStatus && dispatch(setState(nextAgentStatus));
            dispatch(completeCallACW());
        });
    };

    useEffect(() => {
        if (location.pathname.split('/').pop() === 'task') {
            dispatch(clearContact());
        }
    }, [dispatch]);

    useEffect(() => {
        const shouldSetCallInfoToContact = (inCall || (!inCall && showAfterCallWork)) && channel !== 'VOICE';
        if (shouldSetCallInfoToContact) {
            dispatch(setSelectedCall());
        }
    }, [dispatch]);

    return (
        <HomeContext.Provider value={{ diallerOpened, setDiallerOpened }}>
            <section aria-label="Home page" className="sa-dialler-grid">
                {softphoneEnabled && !hasMic && (
                    <Popup center noShadow={false}>
                        <div className="row column auto">
                            <h1>Can&apos;t find an active microphone</h1>
                            <ul>
                                <li>Check that you have microphone access</li>
                                <li>
                                    If you have allowed access, try unplugging your headphone from the PC and trying it
                                    in another USB port.
                                </li>
                                <li>
                                    This error can also be caused by your PC running slowly. If your PC is slow, try
                                    closing as many open programs as possible.
                                </li>
                                <li>You can also try refreshing the page to fix this issue.</li>
                            </ul>
                            <Button styling="primary" onClick={clearFails}>
                                OK
                            </Button>
                        </div>
                    </Popup>
                )}
                <div
                    className={classNames({
                        'panel panel-call fixed-height': true,
                        'after-call-work': showAfterCallWork,
                    })}
                >
                    {showAfterCallWork ? <AfterCallWork /> : <Dialler />}

                    {isMonitoringOrBarging && isEnhancedMonitoringEnabled && <Monitoring connections={connections} />}

                    {hasContact && inCall && !showAfterCallWork && <CallRecordingControl />}

                    {showContactLog ? (
                        <>
                            {hasFeature(AppFeatures.DPA) && <DPA />}
                            {hasFeature(AppFeatures.CUSTOMER_INPUTS) && <CustomerInputs />}
                            <ContactLog channel="VOICE" acw={showAfterCallWork} onSubmit={onEndACW} />
                        </>
                    ) : (
                        (!isMonitoringOrBarging || isEnhancedMonitoringEnabled) &&
                        hasFeature(AppFeatures.CALL_HISTORY) && <CallHistory channel="VOICE" />
                    )}
                </div>

                <ContactPanel channel="VOICE" />
                {hasFeature(AppFeatures.REPORT_ISSUE) && <ReportIssue />}
            </section>
        </HomeContext.Provider>
    );
};

export default Home;
